/**
 * Vessel Types Configuration
 * UK Workboat Code Edition 3 vessel types and categories
 */

export const VESSEL_TYPES = {
  // Certificate-based types (WBC3 compliance)
  WORKBOAT: 'workboat',
  LIGHT_DUTY_WORKBOAT: 'light_duty_workboat',
  PILOT_BOAT_DEDICATED: 'pilot_boat_dedicated',
  POLICE_BOAT: 'police_boat',
  NON_SELF_PROPELLED: 'non_self_propelled',
  
  // Workboat with Single Endorsements
  WORKBOAT_TOWING_ENDORSED: 'workboat_towing_endorsed',
  WORKBOAT_PILOT_ENDORSED: 'workboat_pilot_endorsed',
  WORKBOAT_MGO_TRANSFER_ENDORSED: 'workboat_mgo_transfer_endorsed',
  WORKBOAT_HIGH_SPEED_ENDORSED: 'workboat_high_speed_endorsed',
  // NOTE: Tender certificates removed - tenders operate under parent vessel SMS
  
  // Workboat with Multiple Endorsements
  WORKBOAT_TOWING_PILOT: 'workboat_towing_pilot',
  WORKBOAT_TOWING_MGO: 'workboat_towing_mgo',
  WORKBOAT_PILOT_HIGH_SPEED: 'workboat_pilot_high_speed',
  WORKBOAT_MULTIPLE_CUSTOM: 'workboat_multiple_custom',
  
  // Legacy operational types (for migration)
  RIB: 'rib',
  TUG: 'tug',
  PILOT_BOAT: 'pilot',
  POLICE: 'police',
  PASSENGER: 'pax',
  CREW_TRANSFER: 'ctv',
  SURVEY: 'survey',
  SUPPORT: 'support',
  PATROL: 'patrol',
  CARGO: 'cargo',
  UTILITY: 'utility',
  MULTIPURPOSE: 'multipurpose',
  FISHING_SUPPORT: 'fishing_support',
  HYDROGRAPHIC: 'hydrographic',
  DREDGER: 'dredger',
  SAFETY_STANDBY: 'safety_standby',
  DIVE_SUPPORT: 'dive_support',
  
  // Inland waterway specific types
  HIRE_BOAT_SELF_DRIVE: 'hire_self_drive',
  HIRE_BOAT_SKIPPERED: 'hire_skippered',
  TRIP_BOAT: 'trip_boat',
  NARROWBOAT_COMMERCIAL: 'narrowboat_commercial',
  
  // Other/Specialized vessel type - manual configuration
  OTHER: 'other'
};

export const VESSEL_TYPE_LABELS = {
  // Primary WBC3 certificates
  'workboat': 'Standard Workboat Certificate',
  'light_duty_workboat': 'Light Duty Workboat Certificate ⚠️',
  'pilot_boat_dedicated': 'Dedicated Pilot Boat Certificate',
  'police_boat': 'Police Boat Certificate (WBC3 Annex 3)',
  'non_self_propelled': 'Non-Self-Propelled Workboat Certificate',
  
  // Workboat with Single Endorsements
  'workboat_towing_endorsed': 'Workboat Certificate with Towing Endorsement',
  'workboat_pilot_endorsed': 'Workboat Certificate with Pilot Boat Endorsement',
  'workboat_mgo_transfer_endorsed': 'Workboat Certificate with MGO Transfer Endorsement',
  'workboat_high_speed_endorsed': 'Workboat Certificate with High-Speed Endorsement',
  // NOTE: Tender endorsements removed - tenders operate under parent vessel SMS
  
  // Workboat with Multiple Endorsements
  'workboat_towing_pilot': 'Workboat Certificate with Towing + Pilot Endorsements',
  'workboat_towing_mgo': 'Workboat Certificate with Towing + MGO Transfer Endorsements',
  'workboat_pilot_high_speed': 'Workboat Certificate with Pilot + High-Speed Endorsements',
  'workboat_multiple_custom': 'Workboat Certificate with Custom Multiple Endorsements',
  
  // Legacy operational types (for migration)
  'rib': 'Rigid Inflatable Boat (RIB)',
  'tug': 'Tug Boat',
  'pilot': 'Pilot Boat',
  'police': 'Police Boat',
  'pax': 'Passenger Vessel',
  'ctv': 'Crew Transfer Vessel',
  'survey': 'Survey Vessel',
  'support': 'Support Vessel',
  'patrol': 'Patrol Vessel',
  'cargo': 'Cargo Vessel',
  'utility': 'Utility Vessel',
  'multipurpose': 'Multipurpose Workboat',
  'fishing_support': 'Fishing Support Vessel',
  'hydrographic': 'Hydrographic Vessel',
  'dredger': 'Dredger',
  'safety_standby': 'Safety/Standby Boat',
  'dive_support': 'Dive Support Vessel',
  // Inland waterway specific types
  'hire_self_drive': 'Self-Drive Hire Boat',
  'hire_skippered': 'Skippered Hire Boat',
  'trip_boat': 'Trip Boat / Excursion Vessel',
  'narrowboat_commercial': 'Commercial Narrowboat',
  // Other/Specialized vessel type
  'other': 'Other / Specialized Vessel'
};

// Get context-aware vessel type label
export function getVesselTypeLabel(vesselType, operatingStatus) {
  let label = VESSEL_TYPE_LABELS[vesselType] || vesselType;
  
  // Add regulatory context for police boats
  if (vesselType === 'police') {
    if (operatingStatus === 'seagoing') {
      label += ' (WBC3 Annex 3)';
    } else if (operatingStatus === 'inland') {
      label += ' (Enhanced LSA)';
    }
  }
  
  return label;
}

// Vessel types available for seagoing operations
export const SEAGOING_VESSEL_TYPES = [
  // Certificate-based types
  'workboat', 'workboat_towing_endorsed', 'light_duty_workboat', 'pilot_boat_dedicated', 'workboat_pilot_endorsed', 'police_boat',
  // Legacy operational types
  'rib', 'tug', 'pilot', 'police', 'pax', 'ctv', 'survey', 'support',
  'patrol', 'cargo', 'utility', 'multipurpose', 'fishing_support',
  'hydrographic', 'dredger', 'safety_standby', 'dive_support',
  // Other/Specialized type
  'other'
];

// Vessel types available for inland waterway operations
export const INLAND_VESSEL_TYPES = [
  // Certificate-based types (Light Duty available for inland)
  'workboat', 'workboat_towing_endorsed', 'light_duty_workboat',
  // Legacy operational types
  'rib', 'tug', 'pilot', 'police', 'pax', 'survey', 'support', 'patrol', 
  'cargo', 'utility', 'multipurpose', 'fishing_support', 'hydrographic', 
  'dredger', 'safety_standby', 'dive_support', 'hire_self_drive', 
  'hire_skippered', 'trip_boat', 'narrowboat_commercial',
  // Other/Specialized type
  'other'
];

// Helper function to get available vessel types based on operating status
export function getAvailableVesselTypes(operatingStatus) {
  if (operatingStatus === 'inland') {
    return INLAND_VESSEL_TYPES;
  } else if (operatingStatus === 'seagoing') {
    return SEAGOING_VESSEL_TYPES;
  }
  // If no operating status set, show all types
  return Object.keys(VESSEL_TYPE_LABELS);
}

export const VESSEL_CATEGORIES = {
  CAT_0: '0',
  CAT_1: '1',
  CAT_2: '2',
  CAT_3: '3',
  CAT_4: '4',
  CAT_5: '5',
  CAT_6: '6'
};

export const VESSEL_CATEGORY_LABELS = {
  '0': 'Area Category 0 - Unrestricted service',
  '1': 'Area Category 1 - Up to 150 miles from a safe haven',
  '2': 'Area Category 2 - Up to 60 miles from a safe haven',
  '3': 'Area Category 3 - Up to 20 miles from a safe haven',
  '4': 'Area Category 4 - Up to 20 miles from a safe haven, in favourable weather and in daylight',
  '5': 'Area Category 5 - Within 3 miles of land and not more than 3 miles radius from either the point of departure to sea or the seaward boundary of categorised waters in favourable weather',
  '6': 'Area Category 6 - Within 3 miles of land and not more than 3 miles radius from either the point of departure to sea or the seaward boundary of categorised waters, in favourable weather and daylight'
};

// ===== LIGHT DUTY WORKBOAT CERTIFICATE HELPERS =====

// Check if vessel has Light Duty certificate
export function isLightDutyWorkboat(vesselType) {
  return vesselType === 'light_duty_workboat';
}

// Get Light Duty certificate restrictions
export function getLightDutyRestrictions() {
  return {
    name: 'Light Duty Workboat Certificate Restrictions',
    reference: 'WBC3 Section 3.9.3.5',
    restrictions: [
      '❌ NO lifting devices (cranes, davits, hoists)',
      '❌ NO standard towing operations', 
      '❌ NO dangerous goods carriage',
      '📦 CARGO limited to 1000kg maximum',
      '⚖️ NO severe loading conditions'
    ],
    prohibitedOperations: ['lifting', 'towing', 'dangerous_goods'],
    maxCargoWeight: 1000,
    warning: '⚠️ These restrictions are legally binding under WBC3 Section 3.9.3'
  };
}

// Get certificate description for UI
export function getCertificateDescription(vesselType) {
  const descriptions = {
    'workboat': {
      name: 'Standard Workboat Certificate',
      description: 'General commercial workboat operations (lifting, dangerous goods, but NO towing)',
      restrictions: 'Towing operations require separate endorsement',
      reference: 'WBC3 Section 3'
    },
    'workboat_towing_endorsed': {
      name: 'Workboat Certificate with Towing Endorsement',
      description: 'Commercial workboat certified for towing operations + other workboat operations',
      autoOperations: ['TOWING_OPERATIONS'],
      restrictions: 'None - full operational capability including towing',
      reference: 'WBC3 Section 26.1.5'
    },
    'light_duty_workboat': {
      name: 'Light Duty Workboat Certificate',
      description: 'LIMITED workboat operations for vessels certified under other SCV codes',
      restrictions: getLightDutyRestrictions().restrictions,
      reference: 'WBC3 Section 3.9.3.5',
      warning: getLightDutyRestrictions().warning
    },
    'pilot_boat_dedicated': {
      name: 'Dedicated Pilot Boat Certificate',
      description: 'Dedicated pilot boat certification with specialized equipment requirements',
      autoOperations: ['PILOT_SERVICES'],
      restrictions: ['❌ NO petrol engines (WBC3 Table 27.2.3)'],
      reference: 'WBC3 Section 27'
    },
    'workboat_pilot_endorsed': {
      name: 'Workboat + Pilot Boat Endorsement',
      description: 'Standard workboat with pilot boat operational endorsement',
      autoOperations: ['PILOT_SERVICES'],
      restrictions: ['❌ NO petrol engines when operating as pilot boat'],
      reference: 'WBC3 Section 27.2.3'
    },
    'police_boat': {
      name: 'Police Boat Certificate',
      description: 'Police boat certification with enhanced safety requirements',
      autoOperations: ['POLICE_OPERATIONS'],
      restrictions: ['Must be owned or operated by Police Service'],
      reference: 'WBC3 Annex 3'
    },
    'non_self_propelled': {
      name: 'Non-Self-Propelled Workboat Certificate',
      description: 'Certificate for barges, working platforms, and unmanned vessels',
      autoOperations: [],
      restrictions: ['Limited to non-self-propelled operations'],
      reference: 'WBC3 Section 26.5'
    },
    'workboat_mgo_transfer_endorsed': {
      name: 'Workboat Certificate with MGO Transfer Endorsement',
      description: 'Workboat certified for Marine Gas Oil transfer operations per WBC3 29.10.2.1',
      autoOperations: ['MGO_TRANSFER_OPS'],
      restrictions: [],
      reference: 'WBC3 Section 29.10.2.1',
      endorsementRequired: true
    },
    'workboat_high_speed_endorsed': {
      name: 'Workboat Certificate with High-Speed Endorsement',
      description: 'Workboat certified for operations at 20 knots or more, or in planing mode',
      autoOperations: ['HIGH_SPEED_OPS'],
      restrictions: ['RYA/MCA Advanced Powerboat Certificate required', 'Individual inboard seating with vibration control', 'Enhanced stability requirements'],
      reference: 'WBC3 Section 25.4',
      speedThreshold: 20,
      masterQualification: 'RYA/MCA Advanced Powerboat with high-speed vessel training',
      endorsementRequired: true
    },
    // NOTE: Tender certificate descriptions removed - tenders operate under parent vessel SMS
    'workboat_towing_pilot': {
      name: 'Workboat Certificate with Towing + Pilot Endorsements',
      description: 'Workboat certified for both towing and pilot transfer operations',
      autoOperations: ['TOWING_OPERATIONS', 'PILOT_TRANSFER'],
      restrictions: ['❌ NO petrol engines when operating as pilot boat'],
      reference: 'WBC3 Sections 26.1.5 & 27'
    },
    'workboat_towing_mgo': {
      name: 'Workboat Certificate with Towing + MGO Transfer Endorsements',
      description: 'Workboat certified for towing and Marine Gas Oil transfer operations',
      autoOperations: ['TOWING_OPERATIONS', 'MGO_TRANSFER'],
      restrictions: [],
      reference: 'WBC3 Sections 26.1.5 & 29.10'
    },
    'workboat_pilot_high_speed': {
      name: 'Workboat Certificate with Pilot + High-Speed Endorsements',
      description: 'Workboat certified for pilot transfer at high-speed operations (≥20 knots or planing mode)',
      autoOperations: ['PILOT_TRANSFER', 'HIGH_SPEED_OPS'],
      restrictions: ['❌ NO petrol engines', 'RYA/MCA Advanced Powerboat Certificate required', 'Individual inboard seating with vibration control', 'Enhanced stability requirements'],
      reference: 'WBC3 Sections 27 & 25.4',
      speedThreshold: 20,
      masterQualification: 'RYA/MCA Advanced Powerboat with high-speed vessel training',
      endorsementRequired: true
    },
    'workboat_multiple_custom': {
      name: 'Workboat Certificate with Custom Multiple Endorsements',
      description: 'Workboat with user-configured combination of endorsements',
      autoOperations: [],
      restrictions: ['Manual endorsement configuration required'],
      reference: 'WBC3 Multiple Sections',
      warning: '⚠️ Manual endorsement selection required'
    },
    'other': {
      name: 'Other / Specialized Vessel',
      description: 'Custom vessel configuration for specialized operations not covered by standard types',
      autoOperations: [],
      restrictions: ['Requires manual configuration of certificate type and operations'],
      reference: 'User-defined configuration',
      warning: '⚠️ Manual configuration required - select appropriate certificate and operations'
    }
  };
  
  return descriptions[vesselType] || null;
}